import { fetchApi } from '@/app/api';
import { ChecklistFilter } from '@/features/lists/lists.hooks';
import {
  DTO,
  List,
  ListEvent,
  ListSummary,
  parseListDto,
  parseListSummaryDto,
} from '@certhon/models';
import { map } from 'remeda';

/**
 * PostgREST select clause that pulls all list columns plus feedback aggregates.
 *
 * `feedback_total_count` is a count of all feedback rows attached to this list as
 * a proto (template). `feedback_unresolved_count` filters that count by
 * `handled=false`. Both are computed in a single round-trip via embedded
 * resource aggregation. The disambiguating `!list_feedback_proto_list_id_fkey`
 * is required because the feedback table has two FKs into lists (proto and
 * instance). That constraint name is pinned explicitly in
 * database/sql/V2026.04.29.12.34__checklists-api-and-projection-columns.sql
 * so it cannot be silently renamed.
 */
const LIST_SUMMARY_SELECT =
  '*,' +
  'feedback_total_count:feedback!list_feedback_proto_list_id_fkey(count),' +
  'feedback_unresolved_count:feedback!list_feedback_proto_list_id_fkey(count)';

/** Fetch checklist summaries (no events) for table views. */
export async function fetchChecklists(qry?: ChecklistFilter) {
  const lists = await fetchApi('/lists', {
    deleted: 'eq.false',
    select: LIST_SUMMARY_SELECT,
    'feedback_total_count.deleted': 'eq.false',
    'feedback_unresolved_count.handled': 'eq.false',
    'feedback_unresolved_count.deleted': 'eq.false',
    ...qry,
  });
  return lists.map(parseListSummaryDto);
}

/**
 * Fetch a single checklist with events AND its dependencies (templates).
 * Returns the primary list first, followed by dependency lists.
 * This mirrors the legacy API pattern: one request, all lists needed for building.
 */
// FIXME: implement as single roundtrip with single database query using custom controller instead of postgREST with `[ ...requestedListOrLists, ...dependencies ]` pattern
export async function fetchChecklist(listId: string): Promise<List[]> {
  // 1. Fetch the primary list with its dependencies column
  const primaryResults = await fetchApi('/lists', {
    id: `eq.${listId}`,
    deleted: 'eq.false',
    select: `*,events:listevents(id,recnum,type,payload,user_id,created,inserted)`,
    'events.order': 'created.asc,recnum.asc',
  });
  const primaryDtos = primaryResults as unknown as DTO<List>[];
  if (!primaryDtos.length) {
    throw new Error('Could not find list');
  }

  const primary = parseListDto(primaryDtos[0]);
  const depIds: string[] =
    ((primaryDtos[0] as Record<string, unknown>).dependencies as string[]) ??
    [];

  if (depIds.length === 0) {
    return [primary];
  }

  // 2. Fetch all dependencies in one call.
  // Include deleted dependencies so buildList can still resolve them
  // (their events are needed for the build); UI filters them out separately.
  const depResults = await fetchApi('/lists', {
    id: `in.(${depIds.join(',')})`,
    select: `*,events:listevents(id,recnum,type,payload,user_id,created,inserted)`,
    'events.order': 'created.asc,recnum.asc',
  });
  const depDtos = depResults as unknown as DTO<List>[];
  const deps = depDtos.map(parseListDto);

  return [primary, ...deps];
}

/** Soft-delete a list by setting the `deleted` flag. */
// FIXME: softdelete using patch semantics is confusing pattern. ideally: use DELETE /lists?id.eq with instead of trigger rewriting it as a soft delete
export async function deleteList(id: string) {
  await fetchApi(
    '/lists',
    { id: `eq.${id}` },
    {
      method: 'PATCH',
      body: JSON.stringify({ deleted: true }),
    },
  );
}

/** Post new list events. Returns updated lists with events. */
export function postListEvents(newEvents: ListEvent[]) {
  return fetchApi('/listevents', undefined, {
    method: 'POST',
    body: JSON.stringify(newEvents),
  }).then((res) => map(res, parseListDto));
}
